import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ResultStep as DetailedResultStep } from '../../components/estimation/detailed/steps/ResultStep';
import { calculateDetailedEstimate } from '../../utils/detailedestimation/calculator';
import { calculateQuickEstimate } from '../../utils/quickEstimateCalculator';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';

// Dictionnaire de traduction pour les types de bien
const PROPERTY_TYPE_TRANSLATIONS = {
  'house': 'Maison',
  'apartment': 'Appartement', 
  'building': 'Immeuble'
} as const;

// Dictionnaire de traduction pour les niveaux de rénovation
const RENOVATION_LEVEL_TRANSLATIONS = {
  'light': 'Légère',
  'medium': 'Moyenne',
  'heavy': 'Lourde',
} as const;

// Dictionnaire de traduction pour les éléments clés
const KEY_ELEMENTS_TRANSLATIONS = {
  'windows': 'Changement des fenêtres',
  'kitchen': 'Rénovation de la cuisine',
  'bathroom': 'Rénovation de la salle de bain'
} as const;

const ViewEstimate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const savedEstimate = localStorage.getItem('viewEstimate');
    if (!savedEstimate) {
      setError('Estimation non trouvée');
      return;
    }
    try {
      const parsedEstimate = JSON.parse(savedEstimate);
      setEstimate(parsedEstimate);
    } catch (err) {
      setError('Erreur lors du chargement de l\'estimation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleReset = () => {
    navigate('/dashboard');
  };

  // Fonction de traduction pour le type de bien
  const translatePropertyType = (type: string) => {
    const key = type?.toLowerCase() as keyof typeof PROPERTY_TYPE_TRANSLATIONS;
    return PROPERTY_TYPE_TRANSLATIONS[key] || type;
  };

  // Fonction de traduction pour le niveau de rénovation
  const translateRenovationLevel = (level: string) => {
    const key = level?.toLowerCase() as keyof typeof RENOVATION_LEVEL_TRANSLATIONS;
    return RENOVATION_LEVEL_TRANSLATIONS[key] || level;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!estimate) return null;

  // Pour les estimations détaillées
  if (estimate.type === 'detailed') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <DetailedResultStep
          data={estimate}
          estimate={Promise.resolve(calculateDetailedEstimate(estimate))}
          onBack={handleBack}
          onReset={handleReset}
        />
      </div>
    );
  }

  // Pour les estimations rapides
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold mb-6">Résultat de l'estimation rapide</h1>
        
        <div className="space-y-6">
          {/* Informations du bien */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-2">Informations du bien</h2>
            <p>Adresse : {estimate.location?.address} {estimate.location?.postcode} {estimate.location?.city}</p>
            <p>Type : {translatePropertyType(estimate.propertyType)}</p>
            <p>Surface : {estimate.area} m²</p>
          </div>

          {/* Résultat */}
          <div className="bg-primary/5 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Estimation</h2>
            <p className="text-3xl font-bold text-primary">
              {formatPrice(estimate.priceRange.min)} - {formatPrice(estimate.priceRange.max)}
            </p>
          </div>

          {/* Détails */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Détails</h2>
            <div className="space-y-2">
              <p>Niveau de rénovation : {translateRenovationLevel(estimate.renovationLevel)}</p>
              {estimate.keyElements?.windows && <p>• {KEY_ELEMENTS_TRANSLATIONS.windows}</p>}
              {estimate.keyElements?.kitchen && <p>• {KEY_ELEMENTS_TRANSLATIONS.kitchen}</p>}
              {estimate.keyElements?.bathroom && <p>• {KEY_ELEMENTS_TRANSLATIONS.bathroom}</p>}
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              onClick={handleBack}
              className="btn btn-secondary"
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewEstimate;